import React from "react";
import {
  CheckCircle2,
  XCircle,
  Sparkles,
  Activity,
  ArrowRight,
  Mail,
  FileText,
  Shield,
  HelpCircle,
} from "lucide-react";
import styles from "./LandingPage.module.css";
import { useAppContext } from "../App";
import DetectedSetupsSection from "./DetectedSetupsSection.tsx";
import CftcSummaryValueSection from "./CftcSummaryValueSection.tsx";
import PositioningToExecutionHero from "./landing/PositioningToExecutionHero.tsx";

const Section = ({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => (
  <section id={id} className={`${styles.section} ${className}`}>
    <div className={styles.sectionInner}>{children}</div>
  </section>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.sectionLabel}>{children}</div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className={styles.sectionTitle}>{children}</h2>
);

const SectionSubtitle = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.sectionSubtitle}>{children}</p>
);

const MockChartPreview = () => (
  <div className={styles.mockChart}>
    <img 
      src="/cftc-snapshot-preview.png" 
      alt="CFTC Snapshot Preview" 
      className={styles.heroPreviewImage}
    />
  </div>
);

export default function LandingPage() {
  const { setActiveTab, setCftcSubSection, user } = useAppContext();

  const navigateCftc = (section: "summary" | "trend") => {
    setCftcSubSection(section);
    setActiveTab("CFTC Snapshot");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateLogin = () => {
    setCftcSubSection("summary");
    setActiveTab("CFTC Snapshot");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateTradeSetups = () => {
    setActiveTab("Trade Setups");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateTerms = () => {
    setActiveTab("Terms of Service");
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/terms");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigatePrivacy = () => {
    setActiveTab("Privacy Policy");
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/privacy");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateCookies = () => {
    setActiveTab("Cookie Policy");
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/cookies");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className={styles.main}>
      {/* Hero Section */}
      <Section className={styles.heroSection}>
        <div className={styles.heroGlow} />
        <div className={styles.heroContent}>
          <SectionLabel><Sparkles size={14} />Institutional-Grade Context</SectionLabel>
          <div className={styles.heroIntro}>
            <div className={styles.heroIntroTitle}>Market Analysis Tools (MATs)</div>
            <div className={styles.heroIntroSubtitle}>
              Positioning, context, and structure — built for disciplined traders.
            </div>
            <p className={styles.heroIntroBody}>
              A professional market-analysis platform that merges weekly CFTC positioning with intraday structure to deliver
              regime awareness and auditable context — not signals, predictions, or execution. See who is positioned, how
              crowded it is, how it’s shifting, and how macro posture informs intraday setups.
            </p>
          </div>
          <h1 className={styles.heroTitle}>See positioning before price moves.</h1>
          <p className={styles.heroSubtitle}>
            Trade intraday with macro context.
          </p>
          <p className={styles.heroSupport}>
            Weekly CFTC positioning meets real-time intraday structure — built for disciplined traders, not indicator chasers.
          </p>
          <div className={styles.heroCta}>
            <button className={styles.ctaPrimary} onClick={() => navigateCftc("summary")}>
              View Live Snapshot<ArrowRight size={16} />
            </button>
            {!user && (
              <button className={styles.ctaSecondary} onClick={navigateLogin}>
                Login
              </button>
            )}
          </div>
          <p className={styles.heroDisclaimer}>No predictions. No recommendations. Decision support only.</p>
        </div>
         <div className={styles.heroPreview}><MockChartPreview /></div> 
      </Section>

      <PositioningToExecutionHero />

      <div className={styles.socialProof}>
        <span className={styles.socialProofItem}>Built for discretionary traders</span>
        <span className={styles.socialProofItem}>Used by prop desks</span>
        <span className={styles.socialProofItem}>Macro-aware by design</span>
      </div>

      <CftcSummaryValueSection onCtaClick={() => navigateCftc("summary")} />

      <div className={styles.separator} />

      {/* Navigation by intent */}
      <Section id="navigate">
        <div className={styles.textCenter}>
          <SectionLabel>Navigate</SectionLabel>
          <SectionTitle>Choose the view that fits your decision</SectionTitle>
          <SectionSubtitle>Each view is designed to answer a different trading question.</SectionSubtitle>
        </div>
        <div className={styles.navCardGroup}>
          <div className={styles.navGroupLabel}>CFTC Positioning Decision</div>
          <div className={styles.navCardGrid}>
            <div className={styles.navCard}>
              <div className={styles.navCardTitle}>Futures/Option Trends</div>
              <div className={styles.navCardDesc}>
                Long/short positioning trends with regime context for structural positioning decisions.
              </div>
              <button className={styles.navCardAction} onClick={() => navigateCftc("trend")}>
                Open Futures/Option Trends<ArrowRight size={14} />
              </button>
            </div>
            <div className={styles.navCard}>
              <div className={styles.navCardTitle}>Futures/Option Trends</div>
              <div className={styles.navCardDesc}>
                Directional view is selectable inside the trend dashboard.
              </div>
              <button className={styles.navCardAction} onClick={() => navigateCftc("trend")}>
                Open Futures/Option Trends<ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
        <div className={styles.navCardGroup}>
          <div className={styles.navGroupLabel}>Intraday Execution</div>
          <div className={styles.navCardGrid}>
            <div className={`${styles.navCard} ${styles.navCardHighlight}`}>
              <div className={styles.navCardTitle}>Trade Setups</div>
              <div className={styles.navCardDesc}>
                Use intraday structure to time execution while staying aligned with macro regime context.
              </div>
              <button className={styles.navCardAction} onClick={navigateTradeSetups}>
                Open Trade Setups<ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </Section>

      <div className={styles.separator} />

      {/* Problem / Why */}
      <Section id="problem">
        <div className={styles.textCenter}>
          <SectionLabel>Why</SectionLabel>
          <SectionTitle>Most traders see price. Professionals see positioning.</SectionTitle>
          <SectionSubtitle>Institutional positioning meets intuitive design.</SectionSubtitle>
        </div>
        <ul className={styles.bulletList}>
          {[
            "Indicators ignore who is trapped",
            "Positioning data is slow and misused",
            "Intraday tools lack macro context",
          ].map((item) => (
            <li key={item}>
              <CheckCircle2 size={18} />
              {item}
            </li>
          ))}
        </ul>
      </Section>

      <div className={styles.separator} />

      {/* CFTC Snapshot */}
      <Section id="cftc">
        <div className={styles.featureGrid}>
          <div>
            <SectionLabel>CFTC Snapshot</SectionLabel>
            <SectionTitle>Weekly positioning, made usable</SectionTitle>
            <ul className={styles.featureList}>
              <li>Net &amp; Net % of Open Interest</li>
              <li>Positioning extremes (1Y / 3Y percentiles)</li>
              <li>Open Interest participation</li>
              <li>Regime labels: Trend-Aligned, Flow-Driven, Squeeze Watch, Exhaustion</li>
            </ul>
            <ul className={styles.trustList}>
              <li><CheckCircle2 size={16} />Time-anchored (as-of Tuesday)</li>
              <li><CheckCircle2 size={16} />No look-ahead bias</li>
              <li><CheckCircle2 size={16} />Regime-based, not a directional “call”</li>
            </ul>
          </div>
          <div className={styles.mockPanel}>
            <div className={styles.mockTabs}>
              <span className={styles.mockTabActive}>Dealer</span>
              <span className={styles.mockTab}>Asset Manager</span>
              <span className={styles.mockTab}>Leveraged Funds</span>
            </div>
            <div className={styles.mockRow}>
              <div className={styles.mockLabel}>Open Interest</div>
              <div className={styles.mockLine} />
            </div>
            <div className={styles.mockRow}>
              <div className={styles.mockLabel}>Positioning Extremes</div>
              <div className={styles.mockHeatmap} />
            </div>
            <div className={styles.mockBadgeRow}>
              <span className={styles.mockBadgeAccent}>Macro Regime: Squeeze Watch</span>
              <span className={styles.mockBadge}>CFTC as-of Tue</span>
            </div>
            <div className={styles.mockFootnote}>CFTC is weekly (as-of Tuesday, published Friday).</div>
          </div>
        </div>
      </Section>

      <div className={styles.separator} />

      {/* Trade Setups - Hero */}
      <Section id="setups" className={styles.setupsHero}>
        <div className={styles.textCenter}>
          <SectionLabel>Intraday Scanner</SectionLabel>
          <SectionTitle>Spot High-Probability Setups. Before They Move.</SectionTitle>
          <SectionSubtitle>
            Our scanner detects 7 proven intraday patterns in real-time across FX and equity markets—so you can act on setups, not search for them.
          </SectionSubtitle>
          <div className={styles.setupsPreviewContainer}>
            <img 
              src="/trade-setups-preview.png" 
              alt="Trade Setups Preview" 
              className={styles.setupsPreviewImage}
            />
          </div>

          <div className={styles.setupsHeroCta}>
            <button className={styles.ctaPrimary} onClick={navigateTradeSetups}>
              Start Scanning<ArrowRight size={16} />
            </button>
          </div>
          <div className={styles.setupsHeroStats}>
            <span>7 Setup Types</span>
            <span>Real-Time Detection</span>
            <span>Entry/Stop/Target Levels</span>
          </div>
        </div>
      </Section>

      {/* Trade Setups - Benefits */}
      <Section className={styles.setupsBenefits}>
        <div className={styles.textCenter}>
          <SectionLabel>Why Traders Choose Us</SectionLabel>
          <SectionTitle>Stop Guessing. Start Seeing.</SectionTitle>
          <SectionSubtitle>Everything you need to identify, validate, and execute intraday setups with confidence.</SectionSubtitle>
        </div>
        <div className={styles.benefitsGrid}>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}>⚡</div>
            <h3 className={styles.benefitTitle}>Real-Time Detection</h3>
            <p className={styles.benefitDesc}>Scans every candle close. Auto-refresh keeps you ahead of the market.</p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}>🎯</div>
            <h3 className={styles.benefitTitle}>Precise Levels</h3>
            <p className={styles.benefitDesc}>Entry, stop, and targets—calculated from structure, not guessed.</p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}>🧠</div>
            <h3 className={styles.benefitTitle}>Confluence Built In</h3>
            <p className={styles.benefitDesc}>Macro-micro alignment shows when setups have real edge.</p>
          </div>
        </div>
      </Section>

      {/* Trade Setups - Setup Types */}
      <Section className={styles.setupsTypes}>
        <div className={styles.textCenter}>
          <SectionLabel>Features</SectionLabel>
          <SectionTitle>Smarter Scanning. Built for Execution.</SectionTitle>
          <SectionSubtitle>Seven battle-tested setup types, refined with execution layers and session context.</SectionSubtitle>
        </div>
        <div className={styles.setupTypesGrid}>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>📈</span>
            <h4>EMA Pullback</h4>
            <p>Trend continuation at moving averages</p>
          </div>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>🚩</span>
            <h4>Flag & Pennant</h4>
            <p>Consolidation breakouts</p>
          </div>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>📊</span>
            <h4>VWAP Reclaim</h4>
            <p>Institutional level reclaims</p>
          </div>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>↕️</span>
            <h4>Range Fade</h4>
            <p>Extreme reversals at key levels</p>
          </div>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>🪤</span>
            <h4>Failed Breakout</h4>
            <p>Trap plays when breakouts fail</p>
          </div>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>📦</span>
            <h4>Opening Range</h4>
            <p>First-hour breakout with momentum</p>
          </div>
          <div className={styles.setupTypeCard}>
            <span className={styles.setupTypeIcon}>⚡</span>
            <h4>Momentum Reversal</h4>
            <p>Exhaustion patterns at trend extremes</p>
          </div>
        </div>
      </Section>

      <DetectedSetupsSection />

      {/* Trade Setups - Execution Layer */}
      <Section className={styles.setupsExecution}>
        <div className={`${styles.featureGrid} ${styles.featureGridReverse}`}>
          <div className={styles.setupCardDemo}>
            <div className={styles.setupCardHeader}>
              <span className={styles.setupCardDirection}>🔼 EMA Pullback</span>
              <span className={styles.setupCardConfidence}>HIGH ●●●</span>
              <span className={styles.setupCardStatus}>ACTIVE</span>
            </div>
            <div className={styles.setupCardLevels}>
              <span>📍 Entry: 1.0842</span>
              <span>🛑 Stop: 1.0825</span>
              <span>🎯 T1: 1.0865</span>
              <span>🎯 T2: 1.0890</span>
            </div>
            <div className={styles.setupCardMeta}>
              <div>Session: London 🟢 High liquidity</div>
              <div>Confluence: ✓ Macro-Micro Aligned</div>
              <div>FVG Refined: Entry improved by 4 pips</div>
            </div>
            <div className={styles.setupCardReason}>
              <strong>Why it flagged:</strong>
              <ul>
                <li>Price pulled back to EMA 20 after impulse</li>
                <li>Bullish engulfing at dynamic support</li>
                <li>Volume confirming directional intent</li>
              </ul>
            </div>
          </div>
          <div>
            <SectionLabel>Execution Layer</SectionLabel>
            <SectionTitle>Not Just Patterns. Execution Context.</SectionTitle>
            <p className={styles.featureDesc}>
              Every setup includes session context, confidence scoring, and optional FVG refinement—because knowing <em>when</em> to enter matters as much as <em>what</em> to trade.
            </p>
            <ul className={styles.featureList}>
              <li>Session-aware context (Asia / London / New York)</li>
              <li>Liquidity assessment per session</li>
              <li>FVG Execution Layer refines entry/stop</li>
              <li>"Why it flagged" reasoning for every setup</li>
              <li>Lifecycle tracking (Active → Triggered → Expired)</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Trade Setups - Chart Features */}
      <Section className={styles.setupsChart}>
        <div className={styles.featureGrid}>
          <div>
            <SectionLabel>Visualization</SectionLabel>
            <SectionTitle>See the Setup. See the Edge.</SectionTitle>
            <p className={styles.featureDesc}>
              Interactive charts with EMA, VWAP, ORB zones, and setup overlays—all in one view. Toggle what you need. Hide what you don't.
            </p>
            <div className={styles.overlayList}>
              <span className={styles.overlayItem}><CheckCircle2 size={14} /> EMA 9 / EMA 20</span>
              <span className={styles.overlayItem}><CheckCircle2 size={14} /> VWAP</span>
              <span className={styles.overlayItem}><CheckCircle2 size={14} /> Opening Range Box</span>
              <span className={styles.overlayItem}><CheckCircle2 size={14} /> Setup Zones</span>
              <span className={styles.overlayItem}><CheckCircle2 size={14} /> FVG Zones</span>
              <span className={styles.overlayItem}><CheckCircle2 size={14} /> Historical Setups</span>
            </div>
          </div>
          <div className={styles.mockPanel}>
            <div className={styles.mockTitle}>EUR/USD Intraday (5m)</div>
            <div className={styles.mockCandles} />
            <div className={styles.mockOverlayRow}>
              <span>EMA 9</span>
              <span>EMA 20</span>
              <span>VWAP</span>
              <span>ORB Zone</span>
            </div>
            <div className={styles.mockRiskBox}>
              <div className={styles.mockRiskZone} />
            </div>
            <div className={styles.mockFootnote}>Setup zones show entry/stop/target levels</div>
          </div>
        </div>
      </Section>

      <div className={styles.separator} />

      {/* Macro x Micro */}
      <Section id="macro">
        <div className={styles.textCenter}>
          <SectionLabel>Macro × Micro</SectionLabel>
          <SectionTitle>Where macro positioning meets intraday structure</SectionTitle>
          <SectionSubtitle>We don’t mix timeframes. We layer them.</SectionSubtitle>
        </div>
        <div className={styles.macroGrid}>
          <ul className={styles.featureList}>
            <li>CFTC defines the macro regime</li>
            <li>Intraday setups define execution opportunities</li>
            <li>When both align, we surface Macro–Micro Confluence</li>
          </ul>
          <div className={styles.calloutCard}>
            “Same setup, different confidence — depending on macro context.”
          </div>
          <div className={styles.confluenceCard}>
            <div className={styles.confluenceTitle}>EMA Pullback — LONG</div>
            <div className={styles.confluenceBadges}>
              <span className={styles.mockBadge}>Triggered</span>
              <span className={styles.mockBadge}>MED</span>
              <span className={styles.mockBadgeAccent}>MACRO–MICRO ALIGNED</span>
            </div>
            <div className={styles.confluenceLines}>
              <span>Entry: 1.0820</span>
              <span>Stop: 1.0798</span>
              <span>Targets: 1.0860 / 1.0900</span>
            </div>
          </div>
        </div>
      </Section>

      <div className={styles.separator} />

      {/* Who It's For */}
      <Section id="who">
        <div className={styles.twoColCards}>
          <div className={styles.panelCard}>
            <h3>Built for serious traders.</h3>
            <ul>
              <li>Discretionary traders</li>
              <li>Prop desks</li>
              <li>Macro-aware swing traders</li>
              <li>Systematic researchers (pattern research)</li>
            </ul>
          </div>
          <div className={styles.panelCard}>
            <h3>Not for:</h3>
            <ul>
              <li>Indicator followers</li>
              <li>Auto-trading bots</li>
              <li>“90% win-rate” seekers</li>
              <li>Gamified trading apps</li>
            </ul>
          </div>
        </div>
      </Section>

      <div className={styles.separator} />

      {/* Compliance */}
      <Section>
        <div className={styles.complianceCard}>
          <h3>Decision support — not financial advice</h3>
          <ul>
            {["No predictions", "No probabilities", "No automation", "No execution"].map((item) => (
              <li key={item}>
                <XCircle size={18} />
                {item}
              </li>
            ))}
          </ul>
          <div className={styles.complianceFooter}>We surface context. The trader decides.</div>
        </div>
      </Section>

      <div className={styles.separator} />

      {/* Final CTA */}
      <Section className={styles.ctaSection}>
        <div className={styles.ctaCard}>
          <div className={styles.ctaCardGlow} />
          <div className={styles.ctaCardContent}>
            <p className={styles.ctaCardLabel}>Trade with context.</p>
            <h2 className={styles.ctaCardTitle}>Not conviction without evidence.</h2>
            <p className={styles.ctaCardSubtitle}>Market data may be delayed • Educational use only</p>

          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          {/* Footer Top */}
          <div className={styles.footerTop}>
            <div className={styles.footerBrand}>
              <div className={styles.footerLogo}>
                <Activity size={24} />
                <span>4exAI</span>
              </div>
              <p className={styles.footerTagline}>Decision support for serious traders.</p>
            </div>
            
            <div className={styles.footerLinks}>
              <div className={styles.footerLinkGroup}>
                <h4>Product</h4>
                <button onClick={() => navigateCftc("summary")}>CFTC Snapshot</button>
                <button onClick={() => navigateCftc("trend")}>Futures/Option Trends</button>
                <button onClick={navigateTradeSetups}>Trade Setups</button>
              </div>
              
              <div className={styles.footerLinkGroup}>
                <h4>Resources</h4>
                <a href="#"><HelpCircle size={14} /> Documentation</a>
                <a href="#"><FileText size={14} /> Methodology</a>
                <a href="#">Changelog</a>
              </div>
              
              <div className={styles.footerLinkGroup}>
                <h4>Legal</h4>
                <button onClick={navigateTerms}>Terms of Service</button>
                <button onClick={navigatePrivacy}>Privacy Policy</button>
                <button onClick={navigateCookies}>Cookie Policy</button>
              </div>
              
              <div className={styles.footerLinkGroup}>
                <h4>Connect</h4>
                <a href="mailto:support@example.com"><Mail size={14} /> Support</a>
                <a href="#">Twitter / X</a>
              </div>
            </div>
          </div>
          
          {/* Risk Disclaimer */}
          <div className={styles.footerDisclaimer}>
            <div className={styles.disclaimerIcon}><Shield size={18} /></div>
            <div className={styles.disclaimerText}>
              <strong>Risk Disclaimer</strong>
              <p>
                This platform provides decision support tools for informational and educational purposes only. 
                Nothing herein constitutes financial advice, trading recommendations, or solicitation to buy or sell any securities or derivatives. 
                Past patterns do not guarantee future results. You bear full responsibility for your trading decisions.
              </p>
            </div>
          </div>
          
          {/* Footer Bottom */}
          <div className={styles.footerBottom}>
            <div className={styles.footerData}>
              <span>Data: CFTC COT Report (as-of Tuesday, published Friday)</span>
              <span>•</span>
              <span>Market data may be delayed</span>
            </div>
            <div className={styles.footerCopyright}>
              © {new Date().getFullYear()} 4exAI. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
